import { isSameDay } from "date-fns";
import toast from "react-hot-toast";
import {
  CreateAppointmentServiceInput,
  DeleteAppointmentServiceInput,
  UpdateAppointmentServiceInput,
} from "@/__generated__/schema.graphql.types";
import { useUser } from "@/auth/useUser";
import { useConfirm } from "@/components/providers/confirmProvider";
import { useEditMultiProviderAppointmentMutation } from "@/graphql/mutations/editMultiProviderAppointment.graphql.types";
import { AppointmentBasicInfoDocument } from "@/graphql/queries/appointment/appointmentBasicInfo.graphql.types";
import { ServicesForBookingAppointmentQuery } from "@/graphql/queries/appointment/servicesForBookingAppointment.graphql.types";
import { MedspaProvidersQuery } from "@/graphql/queries/medspaProviders.graphql.types";
import { AppointmentStatus } from "@/types";
import useErrorLogger from "@/utils/useErrorLogger";
import useMedspaTimezone from "../common/useMedspaTimezone";

type AvailableService =
  ServicesForBookingAppointmentQuery["medspaServiceMenuItem"][number];

type Provider = MedspaProvidersQuery["medspaByPk"]["userMedspas"][number];

type EditedAppointmentData = {
  id: string;
  status: AppointmentStatus;
  appointmentServices: AppointmentService[];
};

type AppointmentServiceInput = {
  id?: string;
  serviceMenuItem: AvailableService;
  provider: Provider;
  startTime: Date;
  endTime: Date;
};

type AppointmentService = {
  id: string;
} & AppointmentServiceInput;

type EditMultiProviderAppointmentInput = {
  initialAppointment: EditedAppointmentData;
  formAppointmentServices: AppointmentServiceInput[];
  sendCommunication?: boolean;
};

/**
 * Get the changes in the appointment services. Returns in form of deleted services, new services and updated services.
 * @param initialAppointmentServices - The initial appointment services of edited appointment.
 * @param formAppointmentServices - The appointment services from edit form.
 * @param zonedTimeToUtcIso - The function to convert the date to Medspa UTC ISO string.
 * @returns The deleted services, the new services and the updated services.
 *
 * @TODO: Good candidate for unit tests
 */
const getServicesChanges = ({
  initialAppointmentServices,
  formAppointmentServices,
  zonedTimeToUtcIso,
}: {
  initialAppointmentServices: AppointmentService[];
  formAppointmentServices: AppointmentServiceInput[];
  zonedTimeToUtcIso: (date: Date) => string;
}) => {
  const deletedServices: DeleteAppointmentServiceInput[] = [];
  const newServices: CreateAppointmentServiceInput[] = [];
  const updatedServices: UpdateAppointmentServiceInput[] = [];

  for (const initialService of initialAppointmentServices) {
    const appointmentServiceId = initialService.id;
    const formService = formAppointmentServices.find(
      (service) => service.id === appointmentServiceId
    );

    if (!formService) {
      deletedServices.push({ appointmentServiceId });
      continue;
    }

    if (
      initialService.startTime !== formService.startTime ||
      initialService.endTime !== formService.endTime ||
      initialService.provider.id !== formService.provider.id
    ) {
      updatedServices.push({
        appointmentServiceId,
        providerId: formService.provider.id,
        startTime: zonedTimeToUtcIso(formService.startTime),
        endTime: zonedTimeToUtcIso(formService.endTime),
      });
    }
  }

  for (const formService of formAppointmentServices) {
    if (!formService.id) {
      newServices.push({
        serviceMenuItemId: formService.serviceMenuItem.id,
        providerId: formService.provider.id,
        startTime: zonedTimeToUtcIso(formService.startTime),
        endTime: zonedTimeToUtcIso(formService.endTime),
      });
    }
  }

  return { deletedServices, newServices, updatedServices };
};

/**
 * Use this hook to get a function that sends a request to edit a multi-provider appointment.
 *
 * Returned function accepts the initial appointment data, the edited appointment services and the send communication flag.
 * The function calculated which services are deleted, updated and new.
 */
export function useEditMultiProviderAppointmentRequest() {
  const [editMultiProviderAppointment] =
    useEditMultiProviderAppointmentMutation();
  const logError = useErrorLogger();
  const { zonedTimeToUtcIso } = useMedspaTimezone();
  const { medspa } = useUser();
  const { getConfirmWithCheckbox } = useConfirm();

  const getAppointmentDateChanged = (
    initialAppointment: EditedAppointmentData,
    appointmentServices: AppointmentServiceInput[]
  ) => {
    if (
      !initialAppointment.appointmentServices.length ||
      !appointmentServices.length
    ) {
      return false;
    }

    const initialDate = initialAppointment.appointmentServices[0].startTime;
    const appointmentDate = appointmentServices[0].startTime;

    return !isSameDay(initialDate, appointmentDate);
  };

  return async ({
    initialAppointment,
    formAppointmentServices,
    sendCommunication: _sendCommunication,
  }: EditMultiProviderAppointmentInput) => {
    const getConfirmation = async () => {
      if (_sendCommunication !== undefined) {
        return { confirmed: true, sendCommunication: _sendCommunication };
      }

      const appointmentDateChanged = getAppointmentDateChanged(
        initialAppointment,
        formAppointmentServices
      );

      const isCheckedIn =
        initialAppointment?.status === AppointmentStatus.CHECKED_IN;

      const { confirmed, checkboxValue: sendCommunication } =
        await getConfirmWithCheckbox(
          {
            title: "Reschedule the appointment?",
            description: isCheckedIn
              ? "This client has already checked-in. Are you sure you want to reschedule the appointment?"
              : "Are you sure you want to reschedule the appointment?",
            discardButtonText: "Close",
            confirmButtonText: "Confirm",
          },
          {
            label: "Send e-mail and text to the client with this information",
            defaultValue: appointmentDateChanged,
          }
        );

      return { confirmed, sendCommunication };
    };

    const { confirmed, sendCommunication } = await getConfirmation();

    if (!confirmed) return null;

    const { id: appointmentId, appointmentServices } = initialAppointment;
    const { deletedServices, newServices, updatedServices } =
      getServicesChanges({
        initialAppointmentServices: appointmentServices,
        formAppointmentServices,
        zonedTimeToUtcIso,
      });

    try {
      await toast.promise(
        editMultiProviderAppointment({
          variables: {
            appointmentId,
            deletedServices,
            updatedServices,
            newServices,
            sendCommunication,
          },
          refetchQueries: [
            {
              query: AppointmentBasicInfoDocument,
              variables: {
                id: appointmentId,
                medspaId: medspa,
              },
            },
          ],
        }),
        {
          loading: "Rescheduling appointment...",
          success: "Appointment rescheduled successfully!",
          error: "Failed to reschedule the appointment",
        }
      );
    } catch (e) {
      logError(e);
    }
  };
}
